"use client"

import Image from "next/image"
import { useMemo } from "react"
import LocalizedClientLink from "@/modules/common/components/localized-client-link"
import { CollectionWithImages } from "@/lib/data/collections"
import { shuffleAndSlice } from "@/lib/util/shuffle-sample"

const medusaStrapiUrl = process.env.NEXT_PUBLIC_STRAPI_URL || "https://strapi.krek.si"

interface CollectionsSectionProps {
  collections: CollectionWithImages[]
}

/**
 * Helper to safely extract the first available image URL from a Strapi media field.
 */
const getImageUrl = (media: unknown): string | null => {
  if (!media) return null

  if (Array.isArray(media)) {
    const firstImage = media[0] as { url?: string } | undefined
    return firstImage?.url ? `${medusaStrapiUrl}${firstImage.url}` : null
  }

  const m = media as Record<string, unknown> | null
  if (!m) return null
  const data = Array.isArray(m?.data) ? (m.data as unknown[])[0] : m?.data
  const dataObj = data as Record<string, unknown> | undefined
  const directUrl =
    (m?.url as string) || (dataObj?.attributes as Record<string, unknown>)?.url
  return typeof directUrl === "string" ? `${medusaStrapiUrl}${directUrl}` : null
}

const GRID_POSITIONS = [
  "lg:col-span-2 lg:row-span-2",
  "lg:col-span-1 lg:row-span-1",
  "lg:col-span-1 lg:row-span-1",
  "lg:col-span-2 lg:row-span-1",
]

const HEIGHT_CLASSES = [
  "h-64 sm:h-72 lg:h-[400px]",
  "h-64 sm:h-72 lg:h-[195px]",
  "h-64 sm:h-72 lg:h-[195px]",
  "h-64 sm:h-72 lg:h-[200px]",
]

const CollectionsSection = ({ collections }: CollectionsSectionProps) => {
  const idsSignature = useMemo(
    () =>
      (collections ?? [])
        .map((c) => c.id)
        .sort()
        .join("|"),
    [collections]
  )

  const displayCollections = useMemo(() => {
    if (!collections || collections.length === 0) {
      return []
    }
    return shuffleAndSlice(collections, 4)
  }, [idsSignature])

  if (!collections || collections.length === 0) {
    return null
  }

  return (
    <section className="content-container py-12 md:py-16 lg:py-20">
      <div className="mb-8 md:mb-12">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900">
          Nakupuj po kolekcijah
        </h2>
        <p className="mt-2 text-sm md:text-base text-neutral-600 max-w-2xl">
          Kuratirane kolekcije gostinske opreme – porcelan, kozarci, jedilni
          servisi in pripomočki za peko, izbrani za profesionalne kuhinje in
          domače mojstre.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
        {displayCollections.map((collection, index) => {
          const imgUrl =
            getImageUrl(collection.heroImage) ||
            getImageUrl(collection.bannerImage) ||
            getImageUrl(collection.image)

          const handle = collection.handle || collection.id || "#"
          const gridPosition = GRID_POSITIONS[index] || ""
          const heightClass = HEIGHT_CLASSES[index] || "h-64"
          const title = collection.title || ""

          return (
            <LocalizedClientLink
              href={`/collections/${handle}`}
              key={collection.id || handle}
              className={`group relative block overflow-hidden rounded-2xl ${gridPosition}`}
            >
              <div
                className={`relative w-full ${heightClass} overflow-hidden rounded-2xl`}
              >
                {imgUrl ? (
                  <Image
                    src={imgUrl}
                    alt={title}
                    fill
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    className="object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                    priority={index < 2}
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-100 via-amber-50 to-orange-100" />
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" />

                <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-white/30 rounded-tr-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-white/30 rounded-bl-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-7 lg:p-8">
                  <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white tracking-tight drop-shadow-md transform group-hover:translate-y-0 transition-transform duration-500">
                    {title}
                  </h3>

                  <div className="flex items-center gap-2 mt-3 opacity-70 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 delay-100">
                    <span className="text-sm md:text-base text-white font-medium drop-shadow-sm">
                      Razišči
                    </span>
                    <svg
                      className="w-4 h-4 text-white transform group-hover:translate-x-1 transition-transform duration-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </div>
                </div>

                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />
              </div>
            </LocalizedClientLink>
          )
        })}
      </div>

      <div className="mt-10 flex justify-center">
        <LocalizedClientLink
          href="/collections"
          className="inline-flex items-center justify-center rounded-full border border-navy/20 bg-white px-6 py-3 text-sm font-semibold text-navy shadow-sm transition-all hover:border-navy/40 hover:bg-neutral-50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2"
        >
          Poglej vse kolekcije
        </LocalizedClientLink>
      </div>
    </section>
  )
}

export default CollectionsSection
